// Synchronously read a cached Bearer access token from the active auth SDK.
//
// Why sync? Library callbacks like ChatConfig.subscribeHeadersBuilder fire
// on each SSE reconnect attempt and require a synchronous return value.
// The canonical useAuthHeaders() React hook is async (awaits Auth0/MSAL
// silent refresh) and cannot be awaited inside these callbacks.
//
// Strategy: read directly from the SDK's localStorage cache:
//  - Auth0 SPA SDK: keys starting with "@@auth0spajs@@", body.access_token
//  - MSAL (Entra ID): keys containing "-accesstoken-", secret field,
//    filtered to our app API tokens via target contains "access_as_user",
//    expiry-checked via expiresOn epoch
//
// Returns null when no valid token is cached. Callers should omit
// Authorization rather than send "Bearer null".
//
// Originally inlined in PublicDocumentViewer.tsx; extracted for re-use in
// SSE resume reconnect callbacks (Task #604).
export function getSyncAuthToken(): string | null {
  try {
    for (let i = 0; i < localStorage.length; i++) {
      const key = localStorage.key(i);
      if (!key) continue;

      // Auth0 SPA SDK cache
      if (key.startsWith("@@auth0spajs@@")) {
        const raw = localStorage.getItem(key);
        if (!raw) continue;
        const parsed = JSON.parse(raw);
        const token = parsed?.body?.access_token;
        if (typeof token === "string" && token.length > 0) {
          return token;
        }
      }

      // MSAL (Entra ID) cache. Filter to app API tokens via "access_as_user"
      // and respect expiresOn epoch to skip expired entries.
      if (key.toLowerCase().includes("-accesstoken-")) {
        const raw = localStorage.getItem(key);
        if (!raw) continue;
        const parsed = JSON.parse(raw);
        const target = (parsed?.target || "").toLowerCase();
        if (!target.includes("access_as_user")) continue;
        const token = parsed?.secret;
        if (typeof token === "string" && token.length > 0) {
          const expiresOn = parsed?.expiresOn || parsed?.expires_on;
          if (expiresOn) {
            const expiry = Number(expiresOn);
            if (expiry > 0 && expiry < Math.floor(Date.now() / 1000)) {
              continue;
            }
          }
          return token;
        }
      }
    }
  } catch {
    // localStorage may be unavailable or data malformed -- silent fallback
  }
  return null;
}
